// ~24s polling window: Stripe webhook delivery SLO is "minutes, not seconds"
// and lag past 10s is common under load. Anything shorter falls through to
// the user-visible timeout state more often than the modal can recover from.
const STRIPE_RETURN_REFETCH_ATTEMPTS = 24;
// ~60s polling window for flows that depend on async post-purchase work
// (PDF generation runs through TaskGenerateDigitalDownloads, which can lag
// the webhook by half a minute on a busy worker).
export const STRIPE_RETURN_REFETCH_ATTEMPTS_EXTENDED = 60;
const STRIPE_RETURN_REFETCH_BASE_DELAY_MS = 750;
const STRIPE_RETURN_REFETCH_JITTER_MS = 500;

function waitForStripeReturnRefetch(ms: number) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

// Adds randomized jitter so concurrent timeouts don't all retry in lockstep.
function nextRefetchDelayMs() {
  return (
    STRIPE_RETURN_REFETCH_BASE_DELAY_MS +
    Math.floor(Math.random() * STRIPE_RETURN_REFETCH_JITTER_MS)
  );
}

export class StripeReturnPollingTimeoutError<
  T extends unknown[] = unknown[],
> extends Error {
  constructor(readonly lastResponses: T) {
    super("Timed out waiting for the Stripe return state to become visible.");
    this.name = "StripeReturnPollingTimeoutError";
  }
}

export async function refetchStripeReturnUntil<T extends unknown[]>(
  refetch: () => Promise<T>,
  shouldStopRetrying: (responses: T) => boolean,
  maxAttempts: number = STRIPE_RETURN_REFETCH_ATTEMPTS,
) {
  let responses = await refetch();

  for (
    let attempt = 1;
    attempt < maxAttempts && !shouldStopRetrying(responses);
    attempt += 1
  ) {
    // Stripe can redirect before the webhook mirrors the purchase into account state,
    // and the webhook can lag behind the browser by several seconds under normal load.
    await waitForStripeReturnRefetch(nextRefetchDelayMs());
    responses = await refetch();
  }

  if (!shouldStopRetrying(responses)) {
    throw new StripeReturnPollingTimeoutError(responses);
  }

  return responses;
}

export async function refetchStripeReturnUntilSubscriptionVisible<T extends unknown[]>(
  refetch: () => Promise<T>,
  hasVisibleSubscription: (responses: T) => boolean,
) {
  return refetchStripeReturnUntil(refetch, hasVisibleSubscription);
}
